import {JOBFEED_SLICE_NAME} from '@jobseeker/common-core/constants';
import {createAction, createSlice} from '@reduxjs/toolkit';
import {AppState} from '@jobseeker/common-core/types';
import {HYDRATE} from 'next-redux-wrapper';
import {enqueueSnackbar} from 'notistack';
import {showErrorToast} from '../../utils';
import {
  alumniPageJobs,
  fetchCategoryJobFeed,
  fetchIndustryData,
  fetchLocationBasedJobFeed,
  fetchNotInterested,
  fetchRecruiterJobs,
  fetchRecuiterJobFeed,
  fetchSimilarJobs,
  fetchTagJobFeed,
  fetchUserJobFeed,
  findAppliedJobs,
  findCompanySimilarJobs,
  findJobsForHer,
  findSavedJobs,
  markNotInterested,
  saveJob,
  searchJobsbyQuery,
  undoNotInterested,
  unFollowJob,
  unFollowRecruiter,
} from './thunkServices/jobfeed';

import type {
  AppliedJobsInterface,
  Job,
  JobFeedState,
} from '@jobseeker/common-core/types';

const initialState: Partial<JobFeedState> = {
  jobfeed: [],
  filters: {sort: []},
  filterChips: [],
  loadMoreEnabled: null,
  fetchedPageNo: 0,
  isLoading: true,
  industryOptions: [],
  totalJobs: 0,
  count1Year: null,
  isFilterApplied: false,
};

const HyDrate = createAction<AppState>(HYDRATE);

const patchFeedJob = (
  state: Partial<JobFeedState>,
  jobId: number,
  patch: Partial<Job>
) => {
  // jobfeed is typed as a Job | AppliedJobsInterface array union; the
  // not-interested flow only runs on the jobfeed, which holds Jobs.
  const job = (state.jobfeed as Job[] | undefined)?.find(j => j.id === jobId);
  if (job) Object.assign(job, patch);
};

const jobFeedCommonReducer = (state: any, action: any) => {
  let jobfeed: Job[] | AppliedJobsInterface[] =
    action.payload?.jobs ||
    action.payload?.job?.jobfeed ||
    action.payload?.data ||
    [];

  // if (state.fetchedPageNo != action?.meta?.arg?.pageNo) {
  //   jobfeed = state.jobfeed?.concat(jobfeed);
  // }

  if (action?.meta?.arg?.concat) {
    jobfeed = state.jobfeed?.concat(jobfeed);
  }

  return {
    ...state,
    jobfeed,
    loadMoreEnabled: action.payload?.hasMore,
    fetchedPageNo: action?.meta?.arg?.pageNo || 0,
    isLoading: false,
    loadingSource: null,
    isFilterApplied: false,
    // A real fetch clears the end-of-listing flag.
    reachedEnd: false,
    totalJobs: action?.payload?.totalJobs || 0,
    // Page size actually used by the API for this feed (e.g. search returns
    // `limit`). Lets the pager compute the page count against the real size
    // instead of assuming a fixed value.
    pageLimit:
      typeof action?.payload?.limit === 'number' ? action.payload.limit : null,
    count1Year:
      typeof action?.payload?.count1Year === 'number'
        ? action.payload.count1Year
        : null,
  };
};

const jobFeedSlice = createSlice({
  name: JOBFEED_SLICE_NAME,
  initialState,
  reducers: {
    set_initial_state: (state, action) => {
      return {
        ...initialState,
        industryOptions: state.industryOptions,
      };
    },
    set_jobfeed: (state, action) => {
      return jobFeedCommonReducer(state, action);
    },
    set_filters: (state, action) => {
      const {filters, options} = action.payload;

      if (options.showSort) {
        if (!filters.sort || !filters.sort?.length) {
          if (state.filters?.sort?.includes('date') && !options.clearFilter)
            filters.sort = ['relevance'];
          else {
            filters.sort = ['date'];
          }
        }
      }

      return {
        ...state,
        filters: filters,
      };
    },
    set_filter_chips: (state, action) => {
      const {chips} = action.payload;
      return {
        ...state,
        filterChips: chips,
      };
    },
    remove_filter_chip: (state, action) => {
      const {chipLabel} = action.payload;
      return {
        ...state,
        filterChips: state.filterChips?.filter(c => c.label !== chipLabel),
      };
    },
    set_jobfeed_loading: (state, action) => {
      return {
        ...state,
        isLoading: action.payload.isLoading,
        loadingSource: action.payload.loadingSource,
      };
    },
    set_jobfeed_filter_loaded: (state, action) => {
      return {
        ...state,
        isFilterLoaded: action.payload,
      };
    },
    set_filter_applied: (state, action) => {
      return {
        ...state,
        isFilterApplied: action.payload.isFilterApplied,
      };
    },
    set_similarjobs: (state, action) => {
      return jobFeedCommonReducer(state, action);
    },
    hide_job: (state, action) => {
      const jobId = action.payload;
      state.jobfeed = state.jobfeed?.filter(j => j.id != jobId);
    },
    set_loadMore: (state, action) => {
      return {
        ...state,
        loadMoreEnabled: action.payload,
      };
    },
    // The user pressed Next but the next page came back with no jobs — we are at
    // the end of the listing. Do NOT replace the on-screen jobs with the empty
    // page: restore the listing that was showing, flag reachedEnd (the pager
    // reads this to show the EndOfListing message and disable Next), and force
    // loadMoreEnabled false. Cleared by the next real fetch.
    set_reached_end: (state, action) => {
      if (action.payload?.jobs) {
        state.jobfeed = action.payload.jobs;
      }
      state.reachedEnd = true;
      state.loadMoreEnabled = false;
      state.isLoading = false;
      state.loadingSource = undefined;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchUserJobFeed.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });
    builder.addCase(fetchUserJobFeed.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(saveJob.fulfilled, (state, action) => {
      if (state.jobfeed && state.jobfeed?.length > -1) {
        const actionableJob = state.jobfeed.find(
          job => job.id === action.meta.arg.jobId
        );
        enqueueSnackbar(action.payload.message, {variant: 'success'});
        if (actionableJob) {
          return {
            ...state,
            jobfeed: state.jobfeed.map(job => {
              if (job.id === action.meta.arg.jobId) {
                return {...job, saved: action.meta.arg.unsave ? 0 : 1};
              }
              return job;
            }),
          };
        }
      }
    });
    builder.addCase(saveJob.rejected, (state: any, action: any) => {
      showErrorToast(action.payload?.error?.message, action.payload);

      // console.error(action.payload?.error?.message, 'rejected.payload');
    });

    // Not interested is applied optimistically once reasons are submitted, and
    // rolled back if the API call fails.
    builder.addCase(markNotInterested.pending, (state, action) => {
      const {jobId, reasons} = action.meta.arg;
      patchFeedJob(state, jobId, {
        notInterested: true,
        notInterestedReasons: reasons,
      });
    });
    builder.addCase(markNotInterested.fulfilled, (state, action) => {
      const {jobId} = action.meta.arg;
      state.jobfeed = (state.jobfeed as Job[] | undefined)?.filter(
        j => j.id != jobId
      );
      enqueueSnackbar(action.payload?.message, {variant: 'success'});
    });
    builder.addCase(markNotInterested.rejected, (state, action: any) => {
      patchFeedJob(state, action.meta.arg.jobId, {
        notInterested: false,
        notInterestedReasons: [],
      });
      showErrorToast(action.payload?.error?.message, action.payload);
    });

    builder.addCase(undoNotInterested.pending, (state, action) => {
      patchFeedJob(state, action.meta.arg.jobId, {
        notInterested: false,
        notInterestedReasons: [],
      });
    });
    builder.addCase(undoNotInterested.fulfilled, (state, action) => {
      enqueueSnackbar(action.payload?.message, {variant: 'success'});
    });
    builder.addCase(undoNotInterested.rejected, (state, action: any) => {
      patchFeedJob(state, action.meta.arg.jobId, {notInterested: true});
      showErrorToast(action.payload?.error?.message, action.payload);
    });

    builder.addCase(fetchNotInterested.fulfilled, (state, action) => {
      const marked = new Map(
        (action.payload?.data ?? []).map(r => [r.jobId, r.reasons])
      );
      const requested = new Set(action.meta.arg.jobIds);
      (state.jobfeed as Job[] | undefined)?.forEach(job => {
        // Skip jobs the user acted on while this request was in flight.
        if (!requested.has(job.id) || job.notInterested !== undefined) return;
        const reasons = marked.get(job.id);
        // Explicit false marks the job as checked so it isn't fetched again.
        job.notInterested = !!reasons;
        if (reasons) job.notInterestedReasons = reasons;
      });
    });
    builder.addCase(fetchNotInterested.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(unFollowJob.fulfilled, (state, action) => {
      // alert(action.payload.message);
      enqueueSnackbar(action.payload.message, {variant: 'success'});
    });
    builder.addCase(unFollowJob.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(unFollowRecruiter.fulfilled, (state, action) => {
      // alert(action.payload.message);
      enqueueSnackbar(action.payload.message, {variant: 'success'});
    });
    builder.addCase(unFollowRecruiter.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(fetchCategoryJobFeed.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });
    builder.addCase(fetchCategoryJobFeed.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(fetchRecuiterJobFeed.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });
    builder.addCase(fetchRecuiterJobFeed.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(fetchTagJobFeed.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });
    builder.addCase(fetchTagJobFeed.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(fetchLocationBasedJobFeed.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });
    builder.addCase(fetchLocationBasedJobFeed.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(fetchSimilarJobs.fulfilled, (state, action) => {
      return {...jobFeedCommonReducer(state, action), loadMoreEnabled: false};
    });
    builder.addCase(fetchSimilarJobs.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(findSavedJobs.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });

    builder.addCase(findSavedJobs.pending, (state, action) => {
      return console.log('Loading saved jobs');
    });

    builder.addCase(findSavedJobs.rejected, (state, action) => {
      return console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(findAppliedJobs.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });

    builder.addCase(findAppliedJobs.pending, (state, action) => {
      return console.log('Loading Applied jobs');
    });

    builder.addCase(findAppliedJobs.rejected, (state, action) => {
      return console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(findCompanySimilarJobs.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });

    builder.addCase(findCompanySimilarJobs.pending, (state, action) => {
      return console.log('Loading company similar jobs');
    });

    builder.addCase(findCompanySimilarJobs.rejected, (state, action) => {
      return console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(fetchRecruiterJobs.fulfilled, (state, action) => {
      return {
        ...state,
        jobfeed: action.payload.jobs || [],
        loadMoreEnabled: action.payload?.hasMore ?? false,
        totalJobs: action.payload?.totalJobs || 0,
      };
    });
    builder.addCase(fetchRecruiterJobs.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(fetchIndustryData.fulfilled, (state, action) => {
      return {
        ...state,
        industryOptions: action.payload || [],
      };
    });
    builder.addCase(fetchIndustryData.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(findJobsForHer.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });

    builder.addCase(findJobsForHer.pending, (state, action) => {
      console.log('Loading wow jobs');
      return {
        ...state,
        isLoading: true,
      };
    });

    builder.addCase(findJobsForHer.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
      return {
        ...state,
        isLoading: false,
      };
    });

    builder.addCase(searchJobsbyQuery.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });
    builder.addCase(searchJobsbyQuery.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
      console.log(state, 'rejected.payload');
      return {...state, loadMoreEnabled: false, isFilterLoaded: true};
    });
    builder.addCase(alumniPageJobs.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });
    builder.addCase(alumniPageJobs.pending, state => {
      return {
        ...state,
        isLoading: true,
      };
    });
    builder.addCase(alumniPageJobs.rejected, (state, action) => {
      console.error(action.payload, 'alumniPageJobs rejected');
      return {
        ...state,
        isLoading: false,
        loadMoreEnabled: false,
      };
    });
    builder.addCase(HyDrate, (state, action) => {
      if (!(action.payload?.job?.jobfeed?.length > 0)) {
        return state;
      }

      state.jobfeed = action.payload?.job?.jobfeed || [];
      state.loadMoreEnabled = action.payload?.job?.loadMoreEnabled || false;
      state.fetchedPageNo = action.payload?.job?.fetchedPageNo || 0;
      state.totalJobs = action.payload?.job?.totalJobs || 0;
      state.isLoading = action.payload?.job?.isLoading ?? false;
    });
  },
});

const reducer = jobFeedSlice.reducer;

export {initialState, reducer};
